import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import {
  COVERAGE_GAP_SORTS,
  COVERAGE_GAP_STATES,
  type CoverageGap,
  type CoverageGapSort,
  type CoverageGapState,
} from "@shared/types/corpus/api.ts";
import { Section } from "../../../../components/primitives/layout/Section";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../components/primitives/data-display/DataSequence";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import { gaps as spacing } from "../../../../components/primitives/spacing";
import { formatDate } from "../../../../lib/date";
import { COVERAGE_GAP_SORT_LABELS } from "../../../../lib/coverageGapView";
import { ROUTES } from "../../../../route_paths";
import { LibraryPage } from "../LibraryPage";
import { CoverageGapFormDialog } from "./CoverageGapFormDialog";
import { useCoverageGaps } from "./useCoverageGaps";

const STATE_LABELS: Record<CoverageGapState, string> = { open: "Open", closed: "Closed" };

/**
 * Coverage gaps: what the corpus lacks that people want answered, grouped by hand from the claims we
 * checked and could not answer. The gaps are ranked by demand, with the figures it ranks by in view;
 * a gap opens on its own page.
 */
export default function CoverageGaps() {
  const navigate = useNavigate();
  const { filters, setFilters, gaps, createGap } = useCoverageGaps();
  const [creating, setCreating] = useState(false);

  const columns: DataSequenceColumn<CoverageGap>[] = [
    { key: "name", header: "Gap", width: 320, cardRole: "primary", cell: (gap) => gap.name },
    {
      key: "askers",
      header: "People",
      align: "right",
      cell: (gap) => gap.demand.askers.toLocaleString(),
    },
    {
      key: "claims",
      header: "Claims",
      align: "right",
      cell: (gap) => gap.demand.claims.toLocaleString(),
    },
    {
      key: "asked",
      header: "Asked",
      align: "right",
      cell: (gap) => gap.demand.timesAsked.toLocaleString(),
    },
    { key: "last", header: "Last asked", cell: (gap) => formatDate(gap.demand.lastAskedAt) },
    ...(filters.state === "closed"
      ? [
          {
            key: "toTell",
            header: "To tell",
            align: "right" as const,
            cell: (gap: CoverageGap) => gap.toTell.toLocaleString(),
          },
        ]
      : []),
  ];

  return (
    <LibraryPage
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          New gap
        </Button>
      }
    >
      <Section
        action={
          <Stack direction="row" gap={spacing.snug}>
            <TextField
              select
              size="small"
              label="Show"
              value={filters.state}
              onChange={(e) =>
                setFilters({ ...filters, state: e.target.value as CoverageGapState })
              }
              sx={{ minWidth: 120 }}
            >
              {COVERAGE_GAP_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {STATE_LABELS[state]}
                </MenuItem>
              ))}
            </TextField>
            {filters.state === "closed" ? (
              <FormControlLabel
                label="Only with people to tell"
                control={
                  <Checkbox
                    checked={filters.toTell}
                    onChange={(e) => setFilters({ ...filters, toTell: e.target.checked })}
                  />
                }
              />
            ) : null}
            {filters.state === "open" ? (
              <TextField
                select
                size="small"
                label="Rank by"
                value={filters.sort}
                onChange={(e) =>
                  setFilters({ ...filters, sort: e.target.value as CoverageGapSort })
                }
                sx={{ minWidth: 200 }}
              >
                {COVERAGE_GAP_SORTS.map((sort) => (
                  <MenuItem key={sort} value={sort}>
                    {COVERAGE_GAP_SORT_LABELS[sort]}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
          </Stack>
        }
      >
        {gaps.loading ? (
          <LoadingState />
        ) : gaps.error ? (
          <Alert severity="error">{gaps.error}</Alert>
        ) : (
          <DataSequence
            columns={columns}
            rows={gaps.items}
            getRowId={(gap) => gap.id}
            onRowClick={(gap) =>
              navigate(ROUTES.internal.library.coverage.gaps.detail.path({ id: gap.id }))
            }
            emptyMessage={
              filters.state === "closed"
                ? "No gap is closed."
                : "No coverage gap yet. Create one, then put the uncovered claims in it."
            }
          />
        )}
      </Section>

      <CoverageGapFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        title="New coverage gap"
        submitLabel="Create"
        onSubmit={async (name, note) => {
          const gap = await createGap(name, note);
          navigate(ROUTES.internal.library.coverage.gaps.detail.path({ id: gap.id }));
        }}
      />
    </LibraryPage>
  );
}
